import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputKl =
  "text-[13px] border border-[#E7E4DB] rounded-lg px-3 py-2 bg-white text-neutral-800 focus:outline-none focus:border-neutral-400 transition-colors";

const KIND_LABEL = {
  task_assigned: "Sakstildeling",
  test: "Test",
  mention: "@-tagging",
  comment: "Følger-kommentar",
  status: "Statusendring",
  due_soon: "Frist i morgen",
  due_today: "Frist i dag",
  overdue: "Forfalt sak",
};

const TOGGLES = [
  { k: "taskAssigned", tittel: "E-post ved sakstildeling", sub: "Personen varsles når de blir satt som ansvarlig på en sak", testid: "varsler-toggle-tildeling" },
  { k: "commentMention", tittel: "E-post ved @-tagging", sub: "Den som tagges i en kommentar får e-post med kommentaren", testid: "varsler-toggle-mention" },
  { k: "followerUpdates", tittel: "E-post til følgere", sub: "Følgere av en sak varsles om nye kommentarer og statusendringer", testid: "varsler-toggle-folgere" },
  { k: "dueReminders", tittel: "Fristpåminnelser", sub: "Ansvarlig får e-post dagen før frist, på fristdagen og når saken forfaller", testid: "varsler-toggle-frist" },
];

export default function VarslerModal({ onLukk }) {
  const [data, setData] = useState(null);
  const [testTil, setTestTil] = useState("");
  const [sender, setSender] = useState(false);

  const last = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/notifications");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke hente varsler");
    }
  }, []);

  useEffect(() => { last(); }, [last]);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const toggle = async (key) => {
    const ny = !data.settings[key];
    setData((d) => ({ ...d, settings: { ...d.settings, [key]: ny } }));
    try {
      await api.patch("/admin/notifications", { [key]: ny });
      toast.success(ny ? "Varsel på" : "Varsel av");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kunne ikke lagre");
      last();
    }
  };

  const sendTest = async () => {
    if (!testTil.trim() || sender) return;
    setSender(true);
    try {
      await api.post("/admin/notifications/test", { to: testTil.trim() });
      toast.success(`Test-e-post sendt til ${testTil.trim()}`);
      setTestTil("");
      last();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Sending feilet");
      last();
    } finally {
      setSender(false);
    }
  };

  const tid = (iso) => new Date(iso).toLocaleString("nb-NO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4" data-testid="varsler-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl border border-[#E7E4DB] max-h-[82vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-neutral-100 shrink-0">
          <div>
            <h2 className="text-[15px] font-semibold text-neutral-900">E-postvarsler</h2>
            {data && (
              <p className="text-[11.5px] text-neutral-400 mt-0.5 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${data.configured ? "bg-emerald-500" : "bg-red-500"}`} />
                {data.configured ? `Microsoft 365 · ${data.senderEmail}` : "Ikke konfigurert — mangler Microsoft-oppsett"}
              </p>
            )}
          </div>
          <button type="button" onClick={onLukk} className="p-1.5 text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="varsler-lukk-btn">
            <X size={16} />
          </button>
        </div>

        {!data ? (
          <div className="flex items-center justify-center py-14 text-neutral-400"><Loader2 size={18} className="animate-spin" /></div>
        ) : (
          <div className="overflow-y-auto px-5 py-4">
            {TOGGLES.map((tg) => (
              <button
                key={tg.k}
                type="button"
                onClick={() => toggle(tg.k)}
                className="w-full flex items-center justify-between py-2.5 text-left group"
                data-testid={tg.testid}
              >
                <span>
                  <span className="block text-[13px] font-medium text-neutral-800">{tg.tittel}</span>
                  <span className="block text-[11.5px] text-neutral-400 mt-0.5">{tg.sub}</span>
                </span>
                <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ml-4 ${data.settings[tg.k] ? "bg-neutral-900" : "bg-neutral-300"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${data.settings[tg.k] ? "left-[18px]" : "left-0.5"}`} />
                </span>
              </button>
            ))}

            <div className="mt-4 pt-4 border-t border-neutral-100">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-400 mb-2">Send test</p>
              <div className="flex gap-2">
                <input
                  value={testTil}
                  onChange={(e) => setTestTil(e.target.value)}
                  placeholder="navn@firma.no"
                  className={`${inputKl} flex-1`}
                  data-testid="varsler-test-input"
                />
                <button
                  type="button"
                  onClick={sendTest}
                  disabled={!testTil.trim() || sender}
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-neutral-900 text-white rounded-lg px-3.5 py-2 hover:bg-neutral-700 transition-colors disabled:opacity-40"
                  data-testid="varsler-test-send"
                >
                  {sender ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                </button>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-neutral-100">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-400 mb-1">Siste utsendinger</p>
              {(data.log || []).length === 0 && <p className="text-[12px] text-neutral-400 py-3">Ingen e-poster sendt ennå.</p>}
              {(data.log || []).map((l, i) => (
                <div key={i} className="flex items-start gap-2.5 py-2 border-t border-neutral-100 first:border-t-0" data-testid={`varsler-logg-${i}`}>
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${l.status === "accepted" ? "bg-emerald-500" : "bg-red-500"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] text-neutral-700 truncate">
                      <span className="font-medium">{KIND_LABEL[l.kind] || l.kind}</span> · {l.to}
                    </p>
                    {l.error && <p className="text-[11px] text-red-500 mt-0.5 leading-snug">{l.error}</p>}
                  </div>
                  <span className="text-[10.5px] text-neutral-400 shrink-0 tabular-nums">{tid(l.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
